<script lang="ts">
  import type {Video} from '@/types/project';
  import {thumbnail, thumbDraft, step, preview, isMobile, draftStore, project} from '@/store';
  import Swal from 'sweetalert2';
  import {STEP} from '@/types/common';
  import {onMount, tick} from 'svelte';
  import {formatDate, wait} from '@/utils/common';
  import {deleteDraft, restoreImageDraft} from '@/components/thumbnail/draft-utils';
  import ThumbDraftService from '@/services/thumbdraft';
  import CreateImageDialog from '../CreateImageDialog.svelte';

  export let selectedVideo: Video | null;

  function preRestoreImageDraft(draft: any) {
    if ($isMobile && draft.currentStep !== STEP.PREVIEW)
      return alert('Please go to the last step on desktop to open this draft on Mobile');
    restoreImageDraft(draft);
  }

  let openDropdownIndex = -1;
  let isLoadingDrafts = true;
  let drafts: any[] = [];
  let clips: any[] = [];
  let isCreateImageOpen = false;

  async function createDraft(isSelected: number = 0) {
    if (!selectedVideo) return;
    thumbnail.reset();

    let type = 'quote';
    if (isSelected) type = 'thumbnail';

    thumbnail.reset();
    await thumbnail.setVideo(selectedVideo);
    await thumbnail.setType(type);
    await tick();
    await wait(100);

    Swal.fire({
      titleText: 'Info',
      text: `Loading ${type} please wait...`,
      icon: 'info',
      cancelButtonText: 'Cancel',
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: () => !Swal.isLoading(),
      allowEnterKey: () => !Swal.isLoading(),
      allowEscapeKey: () => !Swal.isLoading()
    });
    await thumbDraft.createNewDraft($thumbnail, undefined, undefined, type).then(() => {
      thumbnail
        .setVideo(selectedVideo)
        .then(() => {
          Swal.close();
          step.update(STEP.CHOOSE_TEMPLATE);
        })
        .catch(error => {
          if (error && error.message && error.message !== 'Network Error') return;
          Swal.fire({
            titleText: 'Error',
            text: 'Thumbnail failed to load, please try again',
            icon: 'error'
          });
        });
    });
  }

  function showLoading() {
    Swal.fire({
      title: 'Please wait Saving now',
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: () => !Swal.isLoading(),
      allowEscapeKey: () => !Swal.isLoading(),
      allowEnterKey: () => !Swal.isLoading()
    });
  }

  function openCreateImageDialog() {
    isCreateImageOpen = true;
  }

  function renameProject(clip: any) {
    openDropdownIndex = -1;

    if (!clip) return;

    Swal.fire({
      title: 'Update name of project',
      input: 'text',
      inputValue: clip?.projectName || '',
      showCancelButton: true,
      confirmButtonText: 'Update'
    }).then(async result => {
      if (!result.isConfirmed || result.value == '') return;

      showLoading();

      let drafftResult = false;
      try {
        drafftResult = await thumbDraft.renameDraftClip(clip.id, {Thumbnail: clip.Thumbnail}, result.value);
      } catch (error) {
        drafftResult = false;
      }

      Swal.fire({
        position: 'top-end',
        icon: !drafftResult ? 'error' : 'success',
        title: !drafftResult ? 'Failed to update project' : 'Project updated',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });

      if (drafftResult) {
        clip.projectName = result.value;
        clips = clips; // trigger svelte re-render
      }
    });
  }

  async function duplicateProject(selectedClip: any) {
    openDropdownIndex = -1;
    if (!selectedClip) return;

    showLoading();

    let drafftResult = false;
    try {
      drafftResult = Boolean(await thumbDraft.duplicateDraft(selectedClip.id));
    } catch (error) {
      drafftResult = false;
    }

    Swal.fire({
      position: 'bottom-end',
      icon: !drafftResult ? 'error' : 'success',
      title: !drafftResult ? 'Failed to duplicate project' : 'Project duplicated',
      showConfirmButton: false,
      timer: 1000,
      toast: true
    });

    if (drafftResult) {
      loadClips();
    }
  }

  async function deleteProject(selectedClip: any) {
    openDropdownIndex = -1;

    try {
      const result = await deleteDraft(selectedClip);
      if (!result) return;

      clips = clips.filter(clip => selectedClip !== clip);
      drafts = drafts.filter(draft => selectedClip !== draft);
    } catch (error) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'error',
        title: 'Failed to delete clip',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });
    }
  }

  function openDropDown(index: number) {
    if (openDropdownIndex !== -1) window.removeEventListener('click', closeDropdown);
    setTimeout(() => {
      openDropdownIndex = index;
      window.addEventListener('click', closeDropdown);
    });
  }

  function closeDropdown() {
    openDropdownIndex = -1;
    window.removeEventListener('click', closeDropdown);
  }

  const loadClips = async () => {
    if (!selectedVideo) return (isLoadingDrafts = false);

    try {
      const draftService = new ThumbDraftService();
      const requestedDrafts = await draftService.getVideoDrafts(selectedVideo.id);
      if (!Array.isArray(requestedDrafts.data)) throw 'requestedDrafts is not an array.';
      drafts = requestedDrafts.data;
    } catch (error) {
      console.error('Thumbnail drafts error', error);
    }

    isLoadingDrafts = false;
  };

  onMount(() => {
    loadClips();
    draftStore.resetState();
    project.reset();
    thumbDraft.resetState();
    thumbnail.reset();
    preview.resetState();
  });

  const animationDelay = 500;
</script>

<div class="h-full w-full overflow-auto">
  <CreateImageDialog bind:isCreateImageOpen {createDraft} />
  <div class="flex w-full flex-wrap items-end gap-6 p-6">
    {#if !$isMobile}
      <button
        on:click={openCreateImageDialog}
        class="flex w-[calc(33.3%-1rem)] flex-col self-stretch lg:w-[calc(25%-1.5rem)]"
      >
        <div
          class="flex h-full min-h-[9rem] w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-zinc-200 hover:bg-zinc-100 dark:bg-zinc-800"
        >
          <i class="fa-solid fa-plus text-2xl" />
          <h1 class="truncate pt-1 text-sm text-zinc-700 dark:text-zinc-200 md:font-medium">Create New Image</h1>
        </div>
      </button>
    {/if}
    {#if isLoadingDrafts}
      {#each Array(parseInt((Math.random() * 11 + 3).toFixed(0))) as _, i}
        <div class="w-full lg:w-[calc(25%-1.5rem)]">
          <div
            class="h-auto w-full animate-pulse overflow-hidden rounded-xl bg-zinc-300"
            style="animation-delay: {animationDelay * i}ms;"
          >
            <div class="aspect-Thumbnail h-full w-full" />
          </div>
          <div class="w-full">
            <div
              class="mt-1 animate-pulse rounded-md bg-zinc-300 text-sm md:font-medium"
              style="width: {(Math.random() * 80 + 20).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
            >
              &nbsp;
            </div>
            <div
              class="mt-1 animate-pulse rounded-md bg-zinc-300 text-xs lg:text-sm"
              style="width: {(Math.random() * 90 + 10).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
            >
              &nbsp;
            </div>
          </div>
        </div>
      {/each}
    {:else if drafts.length > 0 || clips.length > 0}
      {#each drafts as draft, index}
        <div class="w-full lg:w-[calc(25%-1.5rem)]">
          <button
            on:click={() => preRestoreImageDraft(draft)}
            class="relative h-auto w-full overflow-hidden rounded-xl border border-transparent bg-zinc-100 hover:border-rose-500 dark:bg-zinc-700"
          >
            <div class="absolute z-10 flex h-full w-full items-center justify-center bg-black/50">
              <h1 class="text-xl font-bold text-white">DRAFT</h1>
            </div>
            <img
              class="max-h-[360px] w-full object-cover blur-sm {draft.isProject ? '' : 'grayscale'}"
              src={draft?.project?.template?.preview ? draft?.project?.template?.preview : selectedVideo?.preview}
              alt="Thumbnail-thumbnail"
            />
          </button>
          <div class="relative w-full">
            <div class="flex">
              <button on:click={() => preRestoreImageDraft(draft)} class="overflow-hidden pt-1 text-start">
                <h1 class="truncate pt-1 text-sm text-zinc-700 dark:text-zinc-200 md:font-medium">
                  {draft.projectName || 'Untitled Project'}
                </h1>
                <div class="flex gap-4">
                  <h2 class="truncate pt-1 text-xs text-zinc-500 lg:text-sm">
                    {formatDate(draft.updatedAt)}
                  </h2>
                  <span
                    class="ml-auto inline-block self-center rounded-lg px-2 py-1 text-xs font-bold text-white {draft.type ===
                    'thumbnail'
                      ? 'bg-primary'
                      : 'bg-secondary'}">{draft.type === 'thumbnail' ? 'Thumbnail' : 'Quote'}</span
                  >
                </div>
              </button>
              <div class="ml-auto flex items-center px-2">
                <button class="px-2" on:click={() => openDropDown(openDropdownIndex === index ? -1 : index)}>
                  <i class="fa-solid fa-ellipsis-vertical text-xl" />
                </button>
              </div>
            </div>
            <div
              class="absolute z-20 w-full {openDropdownIndex === index
                ? ''
                : 'hidden'} grid rounded-lg border border-zinc-200 bg-white text-sm shadow-md"
            >
              <button on:click={() => duplicateProject(draft)} class="px-4 py-2 hover:text-rose-500">
                {draft.isProject ? 'Duplicate Project' : 'Duplicate Draft'}
              </button>
              <button on:click={() => renameProject(draft)} class="px-4 py-2 hover:text-rose-500">
                {draft.isProject ? 'Rename Project' : 'Rename Draft'}
              </button>
              <button on:click={() => deleteProject(draft)} class="px-4 py-2 hover:text-rose-500">
                {draft.isProject ? 'Delete Project' : 'Delete Draft'}
              </button>
            </div>
          </div>
        </div>
      {/each}
    {/if}
  </div>
</div>
